'use client';

import { entityUrl } from '@/lib/entity-url';
import React, { useState, useEffect, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { TwitterFeed } from '@/components/TwitterFeed';
import DirectorySearch from '@/components/ui/DirectorySearch';
import TrendingEntitiesSidebar from '@/components/social/TrendingEntitiesSidebar';
import { ExternalLink, BadgeCheck, ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { tagService } from '@/lib/tag-service';
import { EntityType, Tag, EntitySearchData } from '@/types/tags';
import { Icon, Mono } from '@/components/dashboard/atoms';
import { ENTITY_TYPES, type EntityTypeKey } from '@/components/dashboard/format';
import { ProfilePulse } from '@/components/admin/TwitterDashboard/ProfilePulse';
import ChainLensChip from '@/components/hub/ChainLensChip';
import { isChainId, getChain } from '@/lib/chains';
import DashboardSocialView from '@/components/social/dashboard/DashboardSocialView';
import { useEntityProfileOpener } from '@/components/dashboard/EntityPreviewDrawer';
import type { DeskTapeDay } from '@/components/social/dashboard/desk-tape';

// Canonical chip order for the in-place feed filter — mirrors the Directory.tsx
// convention (entities explorer). Only types present in the tracked roster render.
const FEED_TYPE_ORDER: EntityTypeKey[] = ['project', 'game', 'person', 'organization', 'protocol', 'token'];

// Chip-row label overrides (the tuner prototype says "Orgs" — "Organizations"
// wraps the narrow roster column's chip row).
const FEED_CHIP_LABELS: Partial<Record<EntityTypeKey, string>> = { organization: 'Orgs' };

interface ClientSocialPageProps {
    selectedEntitySlug?: string;
    activeHandles?: number;
    latestUpdate?: string;
    chainId?: string;
    chainLabel?: string;
    chainHandles?: string[];
    /** THE DESK READ + THE TAPE — last ~14 published recap days (server-fetched, newest first). */
    deskTape?: DeskTapeDay[];
    /** ?day= deep link (validated server-side) — scrubs the feed to a past UTC day. */
    selectedDay?: string;
}

export default function ClientSocialPage({ selectedEntitySlug, activeHandles = 0, latestUpdate = 'Live', chainId, chainLabel, chainHandles, deskTape, selectedDay }: ClientSocialPageProps) {
    const router = useRouter();
    // In-shell entity profile (bet F): inside the dashboard shell, entities WITHOUT a
    // Twitter feed open the profile drawer in place instead of bouncing to /tags.
    // Null on the standalone /social page → unchanged navigation there.
    const openEntityProfile = useEntityProfileOpener();
    // Keep social navigation within the CURRENT surface (e.g. `/<chain>/social` inside the
    // dashboard shell) instead of flinging to the global `/social`. Falls back to `/social`
    // when rendered anywhere unexpected. The standalone `/social` page resolves this to
    // `/social`, so its behavior is unchanged.
    // Normalize away the trailing slash (`trailingSlash: true` makes usePathname return
    // `/abstract/social/`) before matching, then keep that normalized base for navigation.
    const pathname = usePathname();
    const normalizedPath = (pathname ?? '').replace(/\/+$/, '');
    const socialBase = normalizedPath.endsWith('/social') ? normalizedPath : '/social';
    const [entities, setEntities] = useState<EntitySearchData[]>([]);
    const [entityTags, setEntityTags] = useState<Tag[]>([]); // Full tags for DirectorySearch
    const [currentEntity, setCurrentEntity] = useState<EntitySearchData | null>(null);
    const [currentEntityFullData, setCurrentEntityFullData] = useState<any>(null); // Full tag data
    const [associatedTagsData, setAssociatedTagsData] = useState<any[]>([]); // Associated tags full data
    const [isLoadingEntities, setIsLoadingEntities] = useState(true);
    const [isLoadingEntityDetails, setIsLoadingEntityDetails] = useState(false);
    const [selectedHandle, setSelectedHandle] = useState<string>('all'); // For handle switcher

    // In-place feed filter (dashboard surface only) — narrows BOTH the roster rail and
    // the chainHandles fed to the tweet feed, entirely client-side. No router.push, and
    // deliberately NO ⌘K binding: the dashboard shell owns ⌘K (this replaced the page's
    // navigate-mode DirectorySearch, which double-bound ⌘K against the shell palette).
    const [feedQuery, setFeedQuery] = useState('');
    const [feedTypeFilter, setFeedTypeFilter] = useState<'all' | string>('all');

    // Type guard to ensure currentEntity is properly typed
    const typedCurrentEntity = currentEntity as EntitySearchData | null;

    // Memoize entity list to prevent unnecessary re-renders
    const memoizedEntities = useMemo(() => entities, [entities]);
    const memoizedEntityTags = useMemo(() => entityTags, [entityTags]);

    const feedFilterActive = feedQuery.trim() !== '' || feedTypeFilter !== 'all';

    // Entities matching the feed filter — name / slug / handle (the Directory.tsx
    // matcher convention, minus `$symbol` — no token rows on this surface).
    const filteredEntities = useMemo(() => {
        if (!feedFilterActive) return entities;
        const q = feedQuery.trim().toLowerCase();
        return entities.filter(
            (e) =>
                (feedTypeFilter === 'all' || e.entityType === feedTypeFilter) &&
                (!q ||
                    e.name.toLowerCase().includes(q) ||
                    e.slug.toLowerCase().includes(q) ||
                    (e.twitterHandles ?? []).some((h) => h.toLowerCase().includes(q))),
        );
    }, [entities, feedQuery, feedTypeFilter, feedFilterActive]);

    // The feed's handle list under the filter: handles of matching entities, kept
    // chain-scoped (intersected with chainHandles when the chain provides them).
    // Inactive filter → the server-provided chainHandles pass through untouched.
    const filteredChainHandles = useMemo(() => {
        if (!feedFilterActive) return chainHandles;
        const chainSet = new Set((chainHandles ?? []).map((h) => h.toLowerCase()));
        const seen = new Set<string>();
        const out: string[] = [];
        for (const e of filteredEntities) {
            for (const h of e.twitterHandles ?? []) {
                const key = h.toLowerCase();
                if (seen.has(key)) continue;
                if (chainSet.size > 0 && !chainSet.has(key)) continue;
                seen.add(key);
                out.push(h);
            }
        }
        return out;
    }, [feedFilterActive, filteredEntities, chainHandles]);

    // Type chips — only types actually present among tracked (handle-bearing) entities.
    const availableFeedTypes = useMemo(() => {
        const present = new Set(
            entities
                .filter((e) => (e.twitterHandles?.length ?? 0) > 0)
                .map((e) => e.entityType as string),
        );
        return FEED_TYPE_ORDER.filter((t) => present.has(t));
    }, [entities]);

    // Per-type tracked counts for the chip row — the prototype's tuner chips carry
    // their population ("Projects 48"), so the filter reads as a roster census.
    const feedTypeCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        let tracked = 0;
        for (const e of entities) {
            if ((e.twitterHandles?.length ?? 0) === 0) continue;
            tracked += 1;
            const t = e.entityType as string;
            counts[t] = (counts[t] ?? 0) + 1;
        }
        return { counts, tracked };
    }, [entities]);

    // Fetch entities ONCE on mount - this list doesn't change often
    useEffect(() => {
        const fetchEntities = async () => {
            try {
                // READ-DENORM: pull the active-entity set from the CDN-cached `/api/entity-index`
                // route (served from the single `entity_index/active` doc) — ZERO browser→Firestore
                // reads. On any non-200 OR fetch throw, fall back to the existing client-SDK
                // getAllTags scan (a CODE PATH, never an env flag), byte-unchanged.
                let allTags: Tag[];
                try {
                    const res = await fetch('/api/entity-index');
                    if (!res.ok) throw new Error(`entity-index ${res.status}`);
                    const data = await res.json();
                    allTags = (data?.entities ?? []) as Tag[];
                } catch {
                    allTags = await tagService.getAllTags({ isActive: true });
                }

                // Filter to only entity tags
                const filteredTags = allTags.filter(tag => tag.entityType);
                setEntityTags(filteredTags);

                // Include ALL entities (with or without Twitter handles)
                // We'll handle navigation logic based on Twitter handle availability
                const entityList = filteredTags.map(tag => ({
                    id: tag.id,
                    name: tag.name,
                    slug: tag.slug,
                    entityType: tag.entityType as EntityType,
                    twitterHandles: tag.twitterHandles || [],
                    primaryImage: tag.images?.find(img => img.isPrimary)?.url || tag.images?.[0]?.url,
                }));

                setEntities(entityList);
            } catch (error) {
                console.error('Error fetching entities:', error);
            } finally {
                setIsLoadingEntities(false);
            }
        };

        fetchEntities();
    }, []); // Only run once on mount

    // Separate effect for handling entity selection
    useEffect(() => {
        if (!selectedEntitySlug || entities.length === 0) {
            setCurrentEntity(null);
            setCurrentEntityFullData(null);
            setAssociatedTagsData([]);
            setSelectedHandle('all'); // Reset handle filter
            return;
        }

        const fetchEntityDetails = async () => {
            setIsLoadingEntityDetails(true);
            setSelectedHandle('all'); // Reset handle filter when switching entities
            try {
                const selected = entities.find(e => e.slug === selectedEntitySlug);
                setCurrentEntity(selected || null);

                // Fetch full tag data for rich display
                if (selected) {
                    const fullTag = await tagService.getTagBySlug(selectedEntitySlug);
                    setCurrentEntityFullData(fullTag);

                    // Fetch associated tags if they exist
                    if (fullTag?.associatedTags && fullTag.associatedTags.length > 0) {
                        try {
                            // associatedTags are IDs, fetch the full tag objects
                            const assocTagsMap = await tagService.getTagsBatch(fullTag.associatedTags);
                            const assocTags = Array.from(assocTagsMap.values());
                            setAssociatedTagsData(assocTags);
                        } catch (err) {
                            console.error('Error fetching associated tags:', err);
                            setAssociatedTagsData([]);
                        }
                    } else {
                        setAssociatedTagsData([]);
                    }
                }
            } catch (error) {
                console.error('Error fetching entity details:', error);
            } finally {
                setIsLoadingEntityDetails(false);
            }
        };

        fetchEntityDetails();
    }, [selectedEntitySlug, entities]); // Run when selected entity or entities list changes

    // Compose the social URL for an entity-focus + day-scrub state (either optional).
    // Keeps THE TAPE's ?day= and focus mode's ?entity= independent and combinable.
    const socialUrl = (entitySlug?: string | null, day?: string | null) => {
        const params = new URLSearchParams();
        if (entitySlug) params.set('entity', entitySlug);
        if (day) params.set('day', day);
        const qs = params.toString();
        return qs ? `${socialBase}?${qs}` : socialBase;
    };

    const handleEntitySelect = (entityId: string, entitySlug: string) => {
        // Smart navigation: check if entity has Twitter handles
        const entity = entities.find(e => e.id === entityId || e.slug === entitySlug);

        if (!entity) {
            router.push(socialUrl(null, selectedDay));
            return;
        }

        // IF entity has Twitter handles → show social feed
        // IF entity has NO Twitter handles → in-shell profile drawer (dashboard) or
        // the canonical /tags page (standalone /social — no provider, unchanged).
        if (entity.twitterHandles && entity.twitterHandles.length > 0) {
            router.push(socialUrl(entitySlug, selectedDay));
        } else if (openEntityProfile) {
            openEntityProfile(entitySlug);
        } else {
            router.push(entityUrl(entitySlug, { trailingSlash: false }));
        }
    };

    const handleClearFilter = () => {
        // Clear entity filter and state (the day scrub, if any, survives)
        setCurrentEntity(null);
        setCurrentEntityFullData(null);
        setAssociatedTagsData([]);
        router.push(socialUrl(null, selectedDay));
    };

    // THE TAPE — scrub to a past UTC day (null = jump back to live). Deep-linkable.
    const handleDaySelect = (day: string | null) => {
        router.push(socialUrl(selectedEntitySlug, day));
    };

    // Embedded in a chain dashboard? The server route already renders the "<Chain>
    // Social" header, so we drop the duplicate "Explore Socials" title + breadcrumb
    // here and keep just a slim search + stats control row.
    const inDashboard = !!(chainId && isChainId(chainId));
    const chain = inDashboard ? getChain(chainId!) : undefined;

    // Dashboard surface → the "Spine & Index" layout (isolated component). The standalone
    // `/social` page falls through to the existing layout below, byte-for-byte unchanged.
    if (inDashboard) {
        // Live count speaks ACCOUNTS (feed language), never "entities": the number of
        // tracked accounts currently feeding the curated feed under the filter.
        const matchedAccounts = feedFilterActive
            ? (filteredChainHandles?.length ?? 0)
            : (chainHandles?.length ?? activeHandles);

        // Token-native in-place filter — THE TUNER (intercepts prototype: filter input
        // + pill type-chips carrying counts, solid-gold active chip). Typing narrows the
        // roster rail AND the tweet feed in place — no navigation, no ⌘K (the shell owns
        // ⌘K). Visual contract: docs/design/intercepts-prototype/intercepts.html
        // (.tuner-filter / .type-chips / .tchip).
        const searchSlot = isLoadingEntities ? (
            // Skeleton mirrors the loaded control's shape (search input + count line under
            // it + pill chip row) so the reserved slot is filled and the feed below never
            // reflows when entities resolve.
            <div className="flex flex-col gap-3" aria-hidden>
                <div className="flex flex-col gap-1.5">
                    <div className="h-[38px] w-full animate-pulse rounded-[5px] bg-neutral-200 dark:bg-neutral-800" />
                    <div className="h-[12px] w-24 animate-pulse rounded bg-neutral-200 dark:bg-neutral-800" />
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-[24px] w-16 animate-pulse rounded-full bg-neutral-200 dark:bg-neutral-800" />
                    ))}
                </div>
            </div>
        ) : (
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                    <label className="inline-flex w-full items-center gap-2.5 rounded-[5px] border-[0.5px] border-divider bg-surface px-3 py-2">
                        <span className="text-slate-ds">
                            <Icon name="search" size={14} />
                        </span>
                        <input
                            value={feedQuery}
                            onChange={(e) => setFeedQuery(e.target.value)}
                            placeholder="Filter the roster…"
                            aria-label="Filter the social feed by name or handle"
                            className="min-w-0 flex-1 bg-transparent font-display text-[13px] text-parchment outline-none placeholder:text-slate-ds"
                        />
                    </label>
                    {/* Live count — quiet line UNDER the search, full column width so it
                        never truncates in the narrow roster column. */}
                    <span className="font-mono text-[10px] uppercase tracking-[0.05em] text-parchment-dim">
                        {matchedAccounts} {matchedAccounts === 1 ? 'account' : 'accounts'} in feed
                    </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {(['all', ...availableFeedTypes] as const).map((t) => {
                        const active = feedTypeFilter === t;
                        const label =
                            t === 'all'
                                ? 'All'
                                : FEED_CHIP_LABELS[t as EntityTypeKey] ??
                                  `${ENTITY_TYPES[t as EntityTypeKey]?.label ?? t}s`;
                        const count =
                            t === 'all' ? feedTypeCounts.tracked : (feedTypeCounts.counts[t] ?? 0);
                        return (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setFeedTypeFilter(t)}
                                aria-pressed={active}
                                className={`cursor-pointer rounded-full border-[0.5px] px-2.5 py-[5px] font-mono text-[9.5px] font-semibold uppercase tracking-[0.08em] transition-colors ${
                                    active
                                        ? 'border-scout-gold bg-scout-gold text-ink'
                                        : 'border-divider bg-surface text-parchment-dim hover:border-scout-gold/40 hover:text-parchment'
                                }`}
                            >
                                {label}
                                <span className={active ? 'ml-1.5 opacity-70' : 'ml-1.5 opacity-55'}>
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );

        return (
            <DashboardSocialView
                entities={filteredEntities}
                allEntities={entities}
                isLoadingEntities={isLoadingEntities}
                currentEntity={typedCurrentEntity}
                associatedTags={associatedTagsData}
                selectedHandle={selectedHandle}
                onHandleChange={setSelectedHandle}
                onEntitySelect={handleEntitySelect}
                onClear={handleClearFilter}
                chainLabel={chainLabel}
                chainColor={chain?.color}
                chainHandles={filteredChainHandles}
                feedFilterActive={feedFilterActive}
                directoryHref={chain ? `/${chain.hubSlug ?? chainId}/entities` : '/tags'}
                socialBase={socialBase}
                activeHandles={activeHandles}
                latestUpdate={latestUpdate}
                searchSlot={searchSlot}
                deskTape={deskTape}
                selectedDay={selectedDay}
                onDaySelect={handleDaySelect}
            />
        );
    }

    return (
        <div className="space-y-4">
            {/* Header: Title + Search + Stats */}
            <div className="sticky top-0 z-30 bg-white/80 dark:bg-brand-dark/80 backdrop-blur-md border-b border-gray-200 dark:border-neutral-800 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 py-4">
                <div className="max-w-screen-2xl mx-auto">
                    {chainId && isChainId(chainId) && !currentEntity && (
                        // TODO(multichain): chain-scope this — `/social` index retired, points to primary chain.
                        <ChainLensChip chainId={chainId} clearHref="/abstract/social" />
                    )}
                    {/* Breadcrumbs — standalone /social only (the dashboard route renders
                        its own chain header + the sidebar nav provides context). */}
                    {!inDashboard && (
                    <nav className="flex items-center space-x-2 text-sm text-gray-600 dark:text-zinc-400 mb-4" itemScope itemType="https://schema.org/BreadcrumbList">
                        <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link href="/" className="hover:text-scout-gold transition-colors" itemProp="item">
                                <span itemProp="name">Home</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </span>
                        <span>/</span>
                        <span itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link
                                href={socialBase}
                                className={`hover:text-scout-gold transition-colors ${!currentEntity ? 'text-gray-900 dark:text-zinc-100 font-medium' : ''}`}
                                itemProp="item"
                                onClick={handleClearFilter}
                            >
                                <span itemProp="name">Socials</span>
                            </Link>
                            <meta itemProp="position" content="2" />
                        </span>
                        {currentEntity && (
                            <>
                                <span>/</span>
                                <span className="text-gray-900 dark:text-zinc-100 font-medium" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                                    <span itemProp="name">{currentEntity.name}</span>
                                    <meta itemProp="position" content="3" />
                                </span>
                            </>
                        )}
                    </nav>
                    )}

                    <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                        {/* Left: Title & Search */}
                        <div className="flex flex-col md:flex-row md:items-center gap-4 flex-1 min-w-0">
                            {/* Title — standalone only; the dashboard route already renders
                                the "<Chain> Social" header above this island. */}
                            <div className="flex items-center justify-between md:justify-start gap-4">
                                {!inDashboard && (
                                    <h1 className="text-xl font-bold text-gray-900 dark:text-white whitespace-nowrap flex-shrink-0">
                                        Explore Socials
                                    </h1>
                                )}
                                {/* Mobile Stats (only visible on small screens) */}
                                <div className="md:hidden flex items-center gap-2 text-xs text-gray-500">
                                    <span>{activeHandles} accounts</span>
                                </div>
                            </div>

                            {/* Search Bar — tighter in the dashboard (no title competing for
                                the row); wider standalone to fill the space. */}
                            <div className={inDashboard ? 'w-full md:w-[340px]' : 'w-full md:w-[480px]'}>
                                {isLoadingEntities ? (
                                    <div className="h-10 bg-gray-200 dark:bg-neutral-800 rounded-lg animate-pulse"></div>
                                ) : (
                                    <DirectorySearch
                                        tags={memoizedEntityTags}
                                        mode="navigate"
                                        placeholder="Search entities, X handles..."
                                        className="w-full"
                                        getNavigationUrl={(tag) =>
                                            tag.twitterHandles && tag.twitterHandles.length > 0
                                                ? `${socialBase}?entity=${tag.slug}`
                                                : entityUrl(tag.slug, { trailingSlash: false })
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        {/* Right: Stats (Desktop) */}
                        <div className="hidden md:flex items-center gap-4 text-sm flex-shrink-0">
                            <div className="flex items-center gap-1.5">
                                <span className="text-gray-900 dark:text-white font-medium">{activeHandles}</span>
                                <span className="text-gray-500 dark:text-gray-400">accounts</span>
                            </div>
                            <span className="text-gray-300 dark:text-gray-700">•</span>
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                                <span className="text-gray-500 dark:text-gray-400">{latestUpdate}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            {currentEntity ? (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* LEFT COLUMN: Unified Entity Panel - Cohesive design like Market page */}
                    <div className="lg:col-span-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto scrollbar-hide">
                        <div className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
                            {/* Entity Header */}
                            <div className="p-5 border-b border-neutral-200 dark:border-neutral-800">
                                <div className="flex items-start gap-4">
                                    {/* Entity Image */}
                                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex-shrink-0 overflow-hidden">
                                        {currentEntity.primaryImage ? (
                                            <img
                                                src={currentEntity.primaryImage}
                                                alt={currentEntity.name}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-white">
                                                {currentEntity.name.charAt(0)}
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Entity Name & Type */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <h2 className="font-mono text-xl font-bold text-neutral-900 dark:text-white truncate">
                                                {currentEntity.name}
                                            </h2>
                                            {currentEntity.twitterHandles.length > 0 && (
                                                <BadgeCheck className="w-5 h-5 text-blue-500 dark:text-white fill-blue-500 flex-shrink-0" />
                                            )}
                                        </div>
                                        <p className="font-mono text-sm text-neutral-500 truncate">
                                            {currentEntity.entityType?.toUpperCase()} • {currentEntity.twitterHandles.length} accounts
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Filter by Account */}
                            {currentEntity.twitterHandles.length > 0 && (
                                <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
                                    <div className="font-mono text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-2">
                                        Filter by Account
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        <button
                                            onClick={() => setSelectedHandle('all')}
                                            className={`px-2.5 py-1 text-xs font-mono font-medium rounded-md transition-all ${
                                                selectedHandle === 'all'
                                                    ? 'bg-emerald-500 text-white dark:text-neutral-900'
                                                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                                            }`}
                                        >
                                            All ({currentEntity.twitterHandles.length})
                                        </button>
                                        {currentEntity.twitterHandles.map(handle => (
                                            <button
                                                key={handle}
                                                onClick={() => setSelectedHandle(handle)}
                                                className={`px-2.5 py-1 text-xs font-mono font-medium rounded-md transition-all ${
                                                    selectedHandle === handle
                                                        ? 'bg-emerald-500 text-white dark:text-neutral-900'
                                                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700'
                                                }`}
                                                title={`Filter tweets from @${handle}`}
                                            >
                                                @{handle}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* RECON Intel - AI Analysis (embedded in card) */}
                            {currentEntity.twitterHandles.length > 0 && (
                                <div className="border-b border-neutral-200 dark:border-neutral-800">
                                    <ProfilePulse 
                                        handleId={(selectedHandle === 'all' 
                                            ? currentEntity.twitterHandles[0] 
                                            : selectedHandle
                                        ).toLowerCase()} 
                                        variant="embedded"
                                        showWhenEmpty={false}
                                    />
                                </div>
                            )}

                            {/* Related Entities */}
                            {associatedTagsData.length > 0 && (
                                <div className="px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
                                    <div className="font-mono text-[10px] font-bold text-neutral-500 uppercase tracking-wider mb-2">
                                        Related Entities
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {associatedTagsData.slice(0, 8).map((tag: any) => {
                                            const hasTwitter = tag.twitterHandles && tag.twitterHandles.length > 0;
                                            return (
                                                <button
                                                    key={tag.id}
                                                    onClick={() => handleEntitySelect(tag.id, tag.slug)}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono font-medium rounded-md bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                                                    title={hasTwitter ? 'View Social Feed' : 'View Profile'}
                                                >
                                                    {tag.name}
                                                    {hasTwitter && (
                                                        <svg className="w-2.5 h-2.5 text-sky-500 dark:text-sky-400" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                                                        </svg>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="p-4 space-y-3">
                                <Link
                                    href={entityUrl(currentEntity.slug, { trailingSlash: false })}
                                    className="flex items-center justify-center gap-2 w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-white dark:text-neutral-900 font-mono font-bold text-sm rounded-xl transition-colors"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    View Full Profile
                                </Link>
                                <button
                                    onClick={handleClearFilter}
                                    className="flex items-center justify-center gap-2 w-full py-3 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-700 dark:text-white font-mono font-bold text-sm rounded-xl border border-neutral-300 dark:border-neutral-700 transition-colors"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    All Feeds
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Feed */}
                    <div className="lg:col-span-8 space-y-4">
                        <TwitterFeed
                            handles={selectedHandle === 'all' ? currentEntity?.twitterHandles : [selectedHandle]}
                            maxTweets={50}
                            variant="compact"
                            hideEntityTags={!!currentEntity}
                            title={
                                <div className="flex flex-col">
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">Latest Tweets</span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400 font-normal mt-0.5">
                                        {selectedHandle === 'all' 
                                            ? `From ${currentEntity.twitterHandles.length} tracked ${currentEntity.twitterHandles.length === 1 ? 'account' : 'accounts'}`
                                            : `Showing tweets from @${selectedHandle}`
                                        }
                                    </span>
                                </div>
                            }
                        />
                    </div>
                </div>
            ) : (
                /* Default View — feed-led in the dashboard, with a bounded entity rail. */
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Entity rail. BOUNDED sticky scroll (max-h + overflow) so its bottom
                        (incl. the directory CTA) is always reachable — previously it was a
                        sticky column taller than the viewport, so the endless feed kept its
                        lower half pinned off-screen. In the dashboard the feed leads
                        (lg:order-1) and this rail moves to the right (lg:order-2). */}
                    <div className={`lg:col-span-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto scrollbar-hide ${inDashboard ? 'lg:order-2' : ''}`}>
                        {!isLoadingEntities && (
                            <TrendingEntitiesSidebar
                                entities={memoizedEntities}
                                onEntitySelect={handleEntitySelect}
                                currentEntityId={typedCurrentEntity?.id || undefined}
                                directoryHref={
                                    chainId && isChainId(chainId)
                                        ? `/${getChain(chainId)?.hubSlug ?? chainId}/entities`
                                        : '/tags'
                                }
                            />
                        )}
                    </div>

                    {/* Feed — the page's primary subject; leads on the left in the dashboard. */}
                    <div className={`lg:col-span-8 ${inDashboard ? 'lg:order-1' : ''}`}>
                        <TwitterFeed
                            handles={chainHandles && chainHandles.length > 0 ? chainHandles : undefined}
                            maxTweets={50}
                            variant="compact"
                            hideEntityTags={false}
                            title={
                                <div className="flex flex-col">
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                        {chainLabel ? `${chainLabel} Feed` : 'All Tweets'}
                                    </span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400 font-normal mt-0.5">
                                        {chainLabel
                                            ? `Latest from tracked ${chainLabel} accounts`
                                            : 'Showing latest updates from all tracked accounts'}
                                    </span>
                                </div>
                            }
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
